// ======================================================================
//  職業技能 CC 分析 — 設定模型 & localStorage 持久化
// ======================================================================

/**
 * 單一技能 CC 規則
 *  - skillIds 為空 → 對全部（非被動）命中生效
 *  - selfCCIds / targetCCIds 預設使用 AND 邏輯；設為 "or" 時任一符合即成立
 */
export type SkillCCRule = {
    ruleId: string;
    label: string;
    /** 空陣列 = 全部非被動技能；否則只計這些 SkillId 的命中 */
    skillIds: number[];
    /** 自身 Conditions（Buff on self）的 CCId 列表 */
    selfCCIds: number[];
    /** selfCCIds 的判斷邏輯；undefined 視為 "and"（向下相容） */
    selfCCMode?: "and" | "or";
    /** 目標 TargetConditions（Debuff on target）的 CCId 列表 */
    targetCCIds: number[];
    /** targetCCIds 的判斷邏輯；undefined 視為 "and"（向下相容） */
    targetCCMode?: "and" | "or";
    /**
     * 是否在結果表格中顯示為獨立欄位；undefined 視為 true（向下相容）
     */
    showInTable?: boolean;
};

/** 單一職業設定 */
export type JobCCConfig = {
    id: string;
    name: string;
    /** 59xxx 職業專主技能 ID，用於自動偵測職業 */
    detectionSkillIds: number[];
    rules: SkillCCRule[];
};

// ── 預設職業設定（從 dashboard.vue 遷移，改為 AND 邏輯） ─────────────

export const DEFAULT_JOB_CC_CONFIGS: JobCCConfig[] = [
    {
        id: "archer",
        name: "弓鋼",
        detectionSkillIds: [59060, 59061, 59064],
        rules: [
            {
                ruleId: "archer-all",
                label: "零秒狙擊",
                skillIds: [21002, 59060, 59061],
                selfCCIds: [887],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "archer-1776577558547",
                label: "魔法陣",
                skillIds: [21002, 59061],
                selfCCIds: [],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "archer-1778861800823",
                label: "占星-月亮",
                skillIds: [21002, 59060, 59061, 59062, 59063, 59064],
                selfCCIds: [1023],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "archer-1778862022491",
                label: "占星-物理",
                skillIds: [21002, 59060, 59061, 59062, 59063, 59064],
                selfCCIds: [1016],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "archer-1779120806405",
                label: "星星",
                skillIds: [21002, 59060, 59061, 59064],
                selfCCIds: [1033],
                targetCCIds: [],
                showInTable: true,
            },
        ],
    },
    {
        id: "magicSword",
        name: "魔劍",
        detectionSkillIds: [59023, 59024, 59025, 59026, 59028],
        rules: [
            {
                ruleId: "magicSword-all",
                label: "憤衝覆蓋率",
                skillIds: [59024, 59025, 59026, 59028, 20019, 20002],
                selfCCIds: [],
                targetCCIds: [323],
                showInTable: true,
            },
            {
                ruleId: "magicSword-1776577799470",
                label: "魔法陣",
                skillIds: [20002, 59026, 20019, 59028],
                selfCCIds: [10015, 10020],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "magicSword-1776577856080",
                label: "憤衝",
                skillIds: [20018],
                selfCCIds: [889, 10019],
                targetCCIds: [],
                showInTable: true,
            },
        ],
    },
    {
        id: "darkMage",
        name: "黑魔",
        detectionSkillIds: [59040, 59041, 59042],
        rules: [
            {
                ruleId: "darkMage-all",
                label: "魔力穿刺",
                skillIds: [30202, 30102, 30452, 59042, 59040, 59041],
                selfCCIds: [938],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "darkMage-1776578048363",
                label: "雷鏈",
                skillIds: [59040, 30452],
                selfCCIds: [],
                targetCCIds: [948],
                showInTable: true,
            },
        ],
    },
    {
        id: "lancer",
        name: "槍兵",
        detectionSkillIds: [59100, 59101, 59102, 59103, 59104, 59105, 59106],
        rules: [
            {
                ruleId: "lancer-all",
                label: "騎乘效果",
                skillIds: [59101, 59102, 59103, 59104, 59105, 59106, 20017, 59100],
                selfCCIds: [975],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "lancer-1776578133219",
                label: "憤衝",
                skillIds: [20017, 59100, 59101, 59102, 59103, 59104, 59105, 59106],
                selfCCIds: [],
                targetCCIds: [323],
                showInTable: true,
            },
            {
                ruleId: "lancer-1776578268456",
                label: "魔法陣",
                skillIds: [20017, 59101, 59105],
                selfCCIds: [10093],
                targetCCIds: [],
                showInTable: true,
            },
        ],
    },
    {
        id: "shieldWarrior",
        name: "盾勇",
        detectionSkillIds: [59080, 59081, 59082, 59083, 59084, 59085, 59086],
        rules: [
            {
                ruleId: "shieldWarrior-all",
                label: "全部技能",
                skillIds: [59080, 59081, 59082, 59083, 59084, 59085, 59086],
                selfCCIds: [],
                targetCCIds: [323],
                showInTable: true,
            },
        ],
    },
    {
        id: "bard",
        name: "聖詠",
        detectionSkillIds: [59004],
        rules: [],
    },
    {
        id: "gun",
        name: "雙槍",
        detectionSkillIds: [59120, 59121, 59122, 59123, 59124],
        rules: [
            {
                ruleId: "gun-all",
                label: "槍手之眼",
                skillIds: [59120, 59121, 59122, 59123, 54303, 54305, 54306, 54307],
                selfCCIds: [],
                targetCCIds: [1122],
                showInTable: true,
            },
            {
                ruleId: "gun-1778861680264",
                label: "占星-月亮",
                skillIds: [59120, 59121, 59122, 59123, 54303, 54306, 54307],
                selfCCIds: [1023],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "gun-1778861935021",
                label: "占星-增傷",
                skillIds: [59120, 59121, 59122, 59123, 54303, 54304, 54305, 54306, 54307, 54302],
                selfCCIds: [1016],
                targetCCIds: [],
                showInTable: true,
            },
        ],
    },
    {
        id: "alchemist",
        name: "鍊金",
        detectionSkillIds: [59143, 59144, 59145],
        rules: [
            {
                ruleId: "alchemist-all",
                label: "全部技能",
                skillIds: [35014, 35004, 59143, 59144, 59145],
                selfCCIds: [1146, 1145],
                targetCCIds: [1147, 1144],
                showInTable: true,
            },
        ],
    },
    {
        id: "puppetry",
        name: "人偶",
        detectionSkillIds: [59160, 59161, 59162, 59163, 59164, 59165, 59166, 59167, 59168, 59169],
        rules: [
            {
                ruleId: "puppetry-1785846994236",
                label: "增傷",
                skillIds: [54101, 54102, 54104, 54103, 54105, 54106, 54152, 54151, 54153, 54154, 54156, 54155, 59164, 59165, 59166, 59167, 59168, 59169, 59163, 59162, 59161],
                selfCCIds: [],
                selfCCMode: "or",
                targetCCIds: [1161, 323],
                targetCCMode: "or",
                showInTable: true,
            },
            {
                ruleId: "puppetry-1785847243330",
                label: "魔法陣",
                skillIds: [59161, 59162, 59163, 59164, 59165, 59167, 59168, 59169, 54105, 54101, 54102, 54103, 54104, 54151, 54152, 54153, 54154, 54155],
                selfCCIds: [10085, 10086, 10088, 10084],
                selfCCMode: "or",
                targetCCIds: [],
                showInTable: true,
            },
        ],
    },
    {
        id: "fighter",
        name: "武鬥",
        detectionSkillIds: [59180, 59181, 59182, 59183, 59184, 59185, 59186, 59187],
        rules: [
            {
                ruleId: "fighter-1785847325202",
                label: "憤衝",
                skillIds: [24101, 24102, 24103, 24201, 24302, 24202, 24301, 59180, 59181, 59182, 59185, 59186, 59187],
                selfCCIds: [],
                targetCCIds: [323],
                showInTable: true,
            },
            {
                ruleId: "fighter-1785847488180",
                label: "狂怒",
                skillIds: [24101, 24102, 24103, 24201, 24202, 24301, 24302, 59180, 59181, 59182, 59185, 59186, 59187],
                selfCCIds: [1159],
                targetCCIds: [],
                showInTable: true,
            },
            {
                ruleId: "fighter-1785847530485",
                label: "魔法陣",
                skillIds: [24101, 24102, 24103, 24201, 24202, 24301, 24302, 59180, 59181, 59182, 59185, 59186, 59187],
                selfCCIds: [10065, 10066, 10067, 10068, 10069, 10070, 10071],
                selfCCMode: "or",
                targetCCIds: [],
                showInTable: true,
            },
        ],
    },
];

// ── 深複製工具 ───────────────────────────────────────────────────────

export function deepCloneConfigs(configs: JobCCConfig[]): JobCCConfig[] {
    return configs.map((c) => ({
        ...c,
        detectionSkillIds: [...c.detectionSkillIds],
        rules: c.rules.map((r) => ({
            ...r,
            skillIds: [...r.skillIds],
            selfCCIds: [...r.selfCCIds],
            selfCCMode: r.selfCCMode,
            targetCCIds: [...r.targetCCIds],
            targetCCMode: r.targetCCMode,
        })),
    }));
}

// ── localStorage 持久化 ───────────────────────────────────────────────

const LS_KEY = "skill-cc-config-v1";

export function loadJobCCConfigs(): JobCCConfig[] {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as JobCCConfig[];
            if (Array.isArray(parsed) && parsed.length > 0) {
                // 補上新增的預設職業（不覆蓋使用者已自訂的規則）
                const savedIds = new Set(parsed.map((c) => c.id));
                const added = DEFAULT_JOB_CC_CONFIGS.filter((c) => !savedIds.has(c.id));
                return added.length ? [...parsed, ...deepCloneConfigs(added)] : parsed;
            }
        }
    } catch {
        /* ignore */
    }
    return deepCloneConfigs(DEFAULT_JOB_CC_CONFIGS);
}

export function saveJobCCConfigs(configs: JobCCConfig[]): void {
    localStorage.setItem(LS_KEY, JSON.stringify(configs));
}

export function resetToDefaultConfigs(): JobCCConfig[] {
    return deepCloneConfigs(DEFAULT_JOB_CC_CONFIGS);
}

// ── Shared reactive ref（summary2 與 playerComparison 共用） ─────────

import { ref } from "vue";

/** 全域響應式 CC 設定（所有元件共用同一份，儲存 & 讀取透過此 ref 操作） */
export const jobCCConfigs = ref<JobCCConfig[]>(loadJobCCConfigs());

/**
 * 儲存並更新 shared ref（同步所有使用該 ref 的元件）
 */
export function saveAndSyncJobCCConfigs(configs: JobCCConfig[]): void {
    jobCCConfigs.value = deepCloneConfigs(configs);
    saveJobCCConfigs(configs);
}
